// useEquipmentSurvey.ts
import { computed } from "vue";
import type {
  DeviationSurveyEntryRows,
  ScenarioCalculations
} from "@/composables/scenarioCalculations/calculationTypes";
import { extrapolateEntryRowAtMeasuredDepth } from "@/libs/calculations/deviationSurvey";
import type { Point } from "@incytestudios/ddv";

export function useEquipmentIndexes(
  data: number[][],
  surveyType: DeviationSurveyEntryRows["surveyType"] | null,
  scenarioCalculations: ScenarioCalculations,
  chartedData: Point[],
  xAxisLabel = "x",
  yAxisLabel = "y"
) {
  const dataset = data.map((r, i) => {
    if (!i || i === data.length - 1) {
      return r;
    }
    const md = r[0];
    return extrapolateEntryRowAtMeasuredDepth(
      md,
      data[i - 1],
      data[i + 1],
      surveyType
    );
  });

  const calcs = scenarioCalculations;
  const equipmentStartIdx = computed(() => {
    for (let i = 0; i < dataset.length; i++) {
      const row = dataset[i];
      const md = row[0];
      if (md >= calcs.equipmentLengths.tubingLengthFt.value) {
        return i;
      }
    }
    return -1;
  });
  const equipmentEndIdx = computed(() => {
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const md = row[0];

      if (md >= calcs.equipmentLengths.bottomOfEquipmentDepthFt.value) {
        return i;
      }
    }
    return -1;
  });

  const chartedEquipmentIndexDatasets = computed(() => {
    const indexes: {
      title: string;
      points: Point[];
      xAxisLabel: string;
      yAxisLabel: string;
    }[] = [];
    if (equipmentStartIdx.value > -1) {
      indexes.push({
        title: "Equipment Start",
        points: [chartedData[equipmentStartIdx.value]],
        xAxisLabel,
        yAxisLabel
      });
    }
    if (equipmentEndIdx.value > -1) {
      indexes.push({
        title: "Equipment End",
        points: [chartedData[equipmentEndIdx.value]],
        xAxisLabel,
        yAxisLabel
      });
    }
    return indexes;
  });

  return {
    chartedEquipmentIndexDatasets,
    equipmentEndIdx,
    equipmentStartIdx
  };
}
